import type { TooltipProps } from "@mui/material";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { CSSProperties, KeyboardEventHandler, ReactNode } from "react";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 13,
  },
}));

interface Props {
  readonly label: string;
  readonly helperText?: ReactNode;
  readonly onHelperDoubleClick?: () => void;
  readonly selected: string[];
  readonly values: readonly { readonly value: string; readonly label?: string; readonly tooltip?: string }[];
  readonly disabled?: boolean;
  readonly readonly?: boolean;
  readonly size?: "small" | "medium";
  readonly style?: CSSProperties;
  readonly fullWidth?: boolean;
  readonly error?: boolean;
  readonly onSelect: (id: string[]) => void;
  readonly onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const MultiSelect = ({
  label,
  helperText,
  onHelperDoubleClick,
  selected,
  values,
  disabled,
  readonly,
  size,
  style,
  fullWidth,
  error,
  onSelect,
  onKeyDown,
}: Props) => {
  const selectedValues = values.filter((v) => selected.includes(v.value));
  const labelId = `select-named-label-${label?.replace(" ", "-") ?? "id"}`;

  const handleChange = (event: {
    target: {
      value: string | string[];
    };
  }) => {
    const {
      target: { value },
    } = event;
    onSelect(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  return (
    <FormControl variant="outlined" fullWidth={fullWidth} size={size} style={style}>
      <InputLabel id={labelId} disabled={disabled}>
        {label}
      </InputLabel>
      <MuiSelect
        labelId={labelId}
        label={label}
        size={size}
        disabled={disabled}
        inputProps={{ readOnly: readonly }}
        error={error}
        multiple
        value={selectedValues.map((v) => v.value)}
        fullWidth={fullWidth}
        onChange={handleChange}
        onKeyDown={onKeyDown}
      >
        {values.map((v) => {
          const text = v.label ?? v.value;
          if (v.tooltip) {
            return (
              <MenuItem key={v.value} value={v.value}>
                <LightTooltip key={v.value} title={v.tooltip} placement="right">
                  <div style={{ width: "100%" }}>{text}</div>
                </LightTooltip>
              </MenuItem>
            );
          }
          return (
            <MenuItem key={v.value} value={v.value}>
              {text}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {helperText && (
        <FormHelperText data-testid="select-hint">
          <span
            onDoubleClick={onHelperDoubleClick}
            style={{ cursor: onHelperDoubleClick ? "context-menu" : "default" }}
          >
            {helperText}
          </span>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default MultiSelect;
